import { useState, useEffect } from "react";
import { api, assetUrl, formatDate, type NewsPost } from "@/lib/api";

const CATEGORIES = ["ACTUALIZACIÓN", "ALIANZA", "RESULTADOS", "EXPANSIÓN", "EVENTO", "GENERAL"];

const emptyForm = {
  title: "",
  category: "ACTUALIZACIÓN",
  summary: "",
  content: "",
  image_url: "",
  published: true,
  published_at: new Date().toISOString().slice(0, 10),
};

export default function NoticiasPage() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [uploading, setUploading] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setPosts(await api.getNews());
    } catch {
      setPosts([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.createNews({
      ...form,
      published_at: new Date(form.published_at).toISOString(),
    });
    setForm(emptyForm);
    load();
  };

  const startEdit = (post: NewsPost) => {
    setEditingId(post.id);
    setEditForm({
      title: post.title,
      category: post.category,
      summary: post.summary,
      content: post.content,
      image_url: post.image_url,
      published: post.published,
      published_at: post.published_at.slice(0, 10),
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    await api.updateNews(editingId, {
      ...editForm,
      published_at: new Date(editForm.published_at).toISOString(),
    });
    setEditingId(null);
    load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar esta noticia?")) return;
    await api.deleteNews(id);
    load();
  };

  const handleImageUpload = async (id: string, file: File) => {
    setUploading(id);
    try {
      await api.uploadNewsImage(id, file);
      load();
      if (editingId === id) {
        const updated = await api.getNews();
        const post = updated.find((p) => p.id === id);
        if (post) setEditForm((f) => ({ ...f, image_url: post.image_url }));
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al subir imagen");
    }
    setUploading(null);
  };

  return (
    <div className="space-y-6 animate-[fadeIn_0.4s_ease-out]">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Noticias</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Administra las novedades que aparecen en la sección «Últimas novedades» del sitio público
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={handleCreate} className="card space-y-4 xl:col-span-1">
          <h2 className="font-semibold text-[var(--color-text-primary)]">Nueva noticia</h2>
          <input
            className="input-field"
            placeholder="Título"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
          <select
            className="input-field"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="input-field"
            value={form.published_at}
            onChange={(e) => setForm({ ...form, published_at: e.target.value })}
          />
          <input
            className="input-field"
            placeholder="URL de imagen (opcional)"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
          />
          <textarea
            className="input-field min-h-[80px]"
            placeholder="Resumen (opcional)"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
          />
          <textarea
            className="input-field min-h-[160px]"
            placeholder="Contenido completo"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            Publicada en landing
          </label>
          <button type="submit" className="btn-primary w-full">
            Crear noticia
          </button>
        </form>

        <div className="xl:col-span-2 space-y-4">
          {loading ? (
            <div className="text-center py-12 text-[var(--color-text-muted)]">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
            </div>
          ) : posts.length === 0 ? (
            <p className="text-[var(--color-text-muted)] text-center py-12">No hay noticias registradas</p>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="card">
                {editingId === post.id ? (
                  <form onSubmit={handleUpdate} className="space-y-3">
                    <input
                      className="input-field"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      required
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        className="input-field"
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <input
                        type="date"
                        className="input-field"
                        value={editForm.published_at}
                        onChange={(e) => setEditForm({ ...editForm, published_at: e.target.value })}
                      />
                    </div>
                    <input
                      className="input-field"
                      placeholder="URL imagen"
                      value={editForm.image_url}
                      onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                    />
                    <textarea
                      className="input-field min-h-[60px]"
                      placeholder="Resumen"
                      value={editForm.summary}
                      onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
                    />
                    <textarea
                      className="input-field min-h-[160px]"
                      placeholder="Contenido completo"
                      value={editForm.content}
                      onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                    />
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.published}
                        onChange={(e) => setEditForm({ ...editForm, published: e.target.checked })}
                      />
                      Publicada
                    </label>
                    <div className="flex gap-2">
                      <button type="submit" className="btn-primary flex-1">
                        Guardar
                      </button>
                      <button type="button" onClick={() => setEditingId(null)} className="btn-secondary flex-1">
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex gap-4">
                    {post.image_url && (
                      <img
                        src={assetUrl(post.image_url) || post.image_url}
                        alt=""
                        className="w-28 h-20 object-cover rounded-lg shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-xs font-semibold text-[var(--color-accent-oil)]">{post.category}</span>
                          <h3 className="font-semibold text-[var(--color-text-primary)]">{post.title}</h3>
                          <p className="text-xs text-[var(--color-text-muted)] mt-1">{formatDate(post.published_at)}</p>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${
                            post.published
                              ? "bg-green-500/20 text-green-400"
                              : "bg-gray-500/20 text-gray-400"
                          }`}
                        >
                          {post.published ? "Publicada" : "Borrador"}
                        </span>
                      </div>
                      {post.summary && (
                        <p className="text-sm text-[var(--color-text-secondary)] mt-2 line-clamp-2">{post.summary}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => startEdit(post)} className="text-xs btn-secondary py-1.5 px-3">
                          Editar
                        </button>
                        <label className="text-xs btn-secondary py-1.5 px-3 cursor-pointer">
                          {uploading === post.id ? "Subiendo..." : "Subir imagen"}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploading === post.id}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleImageUpload(post.id, f);
                              e.target.value = "";
                            }}
                          />
                        </label>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="text-xs text-red-400 hover:text-red-300 py-1.5 px-3"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
